import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { API_URL, STATUS_LABELS, STATUS_COLORS, ORDER_STATUSES, formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Wrench, Moon, Sun, Plus, Search, LogOut, LayoutDashboard,
  Package, Settings, Menu, X, Filter, Laptop, User
} from 'lucide-react';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/orders`),
        axios.get(`${API_URL}/stats`)
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.equipment.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getOrdersByStatus = (status) => {
    return filteredOrders.filter(order => order.status === status);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-card border-r border-border z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-4 border-b border-border">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <Wrench className="w-4 h-4 text-primary-foreground" />
              </div>
              {sidebarOpen && <span className="font-mono font-bold">TechFix Pro</span>}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <Link to="/admin">
              <Button variant="secondary" className="w-full justify-start gap-3" data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" />
                {sidebarOpen && 'Dashboard'}
              </Button>
            </Link>
            <Link to="/admin/orders/new">
              <Button variant="ghost" className="w-full justify-start gap-3" data-testid="nav-new-order">
                <Plus className="w-4 h-4" />
                {sidebarOpen && 'Nueva Orden'}
              </Button>
            </Link>
            <Link to="/admin/services">
              <Button variant="ghost" className="w-full justify-start gap-3" data-testid="nav-services">
                <Settings className="w-4 h-4" />
                {sidebarOpen && 'Servicios'}
              </Button>
            </Link>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            {sidebarOpen ? (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <User className="w-4 h-4 text-primary" />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <h1 className="font-mono font-bold text-xl">Dashboard</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Total Órdenes</p>
                  <p className="text-3xl font-mono font-bold">{stats.total}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Hoy</p>
                  <p className="text-3xl font-mono font-bold text-primary">{stats.today}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">En Proceso</p>
                  <p className="text-3xl font-mono font-bold text-orange-500">
                    {(stats.by_status.diagnosis || 0) + (stats.by_status.maintenance || 0) + (stats.by_status.software_test || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Listo p/ Entrega</p>
                  <p className="text-3xl font-mono font-bold text-green-500">{stats.by_status.ready || 0}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por ID, cliente o marca..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-orders"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {ORDER_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/admin/orders/new">
              <Button className="w-full sm:w-auto" data-testid="new-order-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Orden
              </Button>
            </Link>
          </div>

          {/* View Tabs */}
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="mb-4">
              <TabsTrigger value="kanban" data-testid="view-kanban">Kanban</TabsTrigger>
              <TabsTrigger value="table" data-testid="view-table">Tabla</TabsTrigger>
            </TabsList>

            {/* Kanban View */}
            <TabsContent value="kanban">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="kanban-board">
                  {ORDER_STATUSES.filter(s => s !== 'delivered').map(status => (
                    <div key={status} className="kanban-column">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-mono font-bold text-sm">{STATUS_LABELS[status]}</h3>
                        <span className="text-xs bg-muted px-2 py-1 rounded-full">
                          {getOrdersByStatus(status).length}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {getOrdersByStatus(status).map(order => (
                          <Link key={order.order_id} to={`/admin/orders/${order.order_id}`}>
                            <Card className="order-card cursor-pointer hover:border-primary/50" data-testid={`order-card-${order.order_id}`}>
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-mono text-xs font-bold text-primary">
                                    {order.order_id}
                                  </span>
                                  <span className={`status-badge ${STATUS_COLORS[order.status]}`}>
                                    {STATUS_LABELS[order.status]}
                                  </span>
                                </div>
                                <p className="font-medium text-sm mb-1">{order.client.name}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Laptop className="w-3 h-3" />
                                  <span>{order.equipment.brand} {order.equipment.model}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 font-mono">
                                  {formatDate(order.created_at)}
                                </p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                        {getOrdersByStatus(status).length === 0 && (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            Sin órdenes
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Table View */}
            <TabsContent value="table">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-4 font-mono text-sm">ID</th>
                          <th className="text-left p-4 font-mono text-sm">Cliente</th>
                          <th className="text-left p-4 font-mono text-sm">Equipo</th>
                          <th className="text-left p-4 font-mono text-sm">Estado</th>
                          <th className="text-left p-4 font-mono text-sm">Costo Est.</th>
                          <th className="text-left p-4 font-mono text-sm">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map(order => (
                          <tr 
                            key={order.order_id} 
                            className="border-b border-border table-row-hover cursor-pointer"
                            onClick={() => navigate(`/admin/orders/${order.order_id}`)}
                            data-testid={`order-row-${order.order_id}`}
                          >
                            <td className="p-4 font-mono text-sm text-primary">{order.order_id}</td>
                            <td className="p-4">
                              <p className="font-medium">{order.client.name}</p>
                              <p className="text-xs text-muted-foreground">{order.client.phone}</p>
                            </td>
                            <td className="p-4">
                              <p>{order.equipment.brand} {order.equipment.model}</p>
                              <p className="text-xs text-muted-foreground">{order.equipment.type}</p>
                            </td>
                            <td className="p-4">
                              <span className={`status-badge ${STATUS_COLORS[order.status]}`}>
                                {STATUS_LABELS[order.status]}
                              </span>
                            </td>
                            <td className="p-4 font-mono">{formatCurrency(order.estimated_cost)}</td>
                            <td className="p-4 text-sm text-muted-foreground font-mono">
                              {formatDate(order.created_at)}
                            </td>
                          </tr>
                        ))}
                        {filteredOrders.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-muted-foreground">
                              No se encontraron órdenes
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
